/**
 * FeeRevenueChart — Fee revenue area chart using Lightweight Charts (TradingView).
 *
 * Owns its own data fetching via useRevenueHistory hook.
 * Mirrors VolumeChart structure: async dynamic import, resize observer, sample data badge.
 * Shows cumulative fee revenue over time as an area chart.
 *
 * Story 8.4 — Task 2
 */

import type { FeeRevenueHistoryEntry, PriceInterval } from "@repo/shared-types";
import {
	ColorType,
	type IChartApi,
	type ISeriesApi,
	type SeriesOptionsMap,
} from "lightweight-charts";
import { useEffect, useRef, useState } from "react";
import { formatUnits } from "viem";

import { useRevenueHistory } from "../../hooks/api/use-revenue-history";
import { toUTCTimestamp } from "../../lib/chart-utils";
import { getNativeCurrencySymbol } from "../../lib/wagmi-config";
import { TimeRangeSelector } from "../token/TimeRangeSelector";

type FeeRevenueChartProps = {
	tokenId: string;
};

function formatFeeValue(weiString: string): number {
	try {
		return Number(formatUnits(BigInt(weiString), 18));
	} catch {
		return 0;
	}
}

export function FeeRevenueChart({ tokenId }: FeeRevenueChartProps) {
	const [selectedInterval, setSelectedInterval] = useState<PriceInterval>("1D");
	const { data, isLoading, error, refetch } = useRevenueHistory(tokenId, selectedInterval);
	const [chartReady, setChartReady] = useState(false);

	const containerRef = useRef<HTMLDivElement>(null);
	const chartRef = useRef<IChartApi | null>(null);
	const seriesRef = useRef<ISeriesApi<keyof SeriesOptionsMap> | null>(null);
	const observerRef = useRef<ResizeObserver | null>(null);

	const symbol = getNativeCurrencySymbol();

	// Mount/unmount chart — async import matching VolumeChart/HolderChart pattern
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		let cancelled = false;

		async function initChart() {
			const { createChart, AreaSeries } = await import("lightweight-charts");
			if (cancelled || !container) return;

			const chart: IChartApi = createChart(container, {
				width: container.clientWidth,
				height: 200,
				layout: {
					background: { type: ColorType.Solid, color: "transparent" },
					textColor: "#6b7280",
				},
				grid: {
					vertLines: { color: "rgba(100, 100, 100, 0.1)" },
					horzLines: { color: "rgba(100, 100, 100, 0.1)" },
				},
				rightPriceScale: {
					borderVisible: false,
				},
				timeScale: {
					borderVisible: false,
					timeVisible: selectedInterval === "1H" || selectedInterval === "4H",
				},
			});

			const series = chart.addSeries(AreaSeries, {
				lineColor: "#f59e0b",
				topColor: "rgba(245, 158, 11, 0.3)",
				bottomColor: "rgba(245, 158, 11, 0.02)",
				lineWidth: 2,
			});

			chartRef.current = chart;
			seriesRef.current = series;

			const observer = new ResizeObserver((entries) => {
				const entry = entries[0];
				if (entry) {
					chart.applyOptions({ width: entry.contentRect.width });
				}
			});
			observer.observe(container);
			observerRef.current = observer;

			if (!cancelled) setChartReady(true);
		}

		void initChart();

		return () => {
			cancelled = true;
			observerRef.current?.disconnect();
			observerRef.current = null;
			if (chartRef.current) {
				chartRef.current.remove();
				chartRef.current = null;
				seriesRef.current = null;
			}
			setChartReady(false);
		};
	}, [selectedInterval]);

	// Update data — depends on chartReady to avoid race condition
	useEffect(() => {
		const series = seriesRef.current;
		if (!series || !chartReady || !data?.data) return;

		const chartData = data.data.map((entry: FeeRevenueHistoryEntry) => ({
			time: toUTCTimestamp(entry.timestamp),
			value: formatFeeValue(entry.cumulativeAmount),
		}));

		series.setData(chartData);
		chartRef.current?.timeScale().fitContent();
	}, [data, chartReady]);

	const isSeeded = data?.meta?.dataSource === "seeded";

	return (
		<div className="rounded-xl border border-pastel-border bg-white p-4">
			<div className="flex items-center justify-between mb-3">
				<h4 className="text-sm font-semibold text-pastel-black">Your Fee Revenue ({symbol})</h4>
				<div className="flex items-center gap-2">
					<TimeRangeSelector selected={selectedInterval} onIntervalChange={setSelectedInterval} />
					{isSeeded && (
						<span className="text-xs text-pastel-muted bg-yellow-50 px-2 py-0.5 rounded">
							Sample Data
						</span>
					)}
				</div>
			</div>

			{error ? (
				<div className="flex flex-col items-center justify-center h-[200px] text-pastel-muted text-sm gap-2">
					<p>Failed to load fee revenue data</p>
					<button
						type="button"
						onClick={() => void refetch()}
						className="text-pastel-primary text-xs font-medium hover:underline"
					>
						Retry
					</button>
				</div>
			) : isLoading ? (
				<div className="h-[200px] w-full rounded-lg bg-pastel-light-gray animate-pulse" />
			) : data?.data?.length === 0 ? (
				<div className="flex items-center justify-center h-[200px] text-pastel-gray text-sm">
					No fee revenue data available
				</div>
			) : (
				<div ref={containerRef} className="w-full h-[200px]" data-testid="fee-revenue-chart" />
			)}
		</div>
	);
}
